import { inject, Injectable, DestroyRef } from '@angular/core';
import { CHANGE_TRACKER } from '@app/core/tokens/infrastructure.tokens';
import { CreateBackupUseCase, GetLastBackupTimeUseCase } from '@app/domain/use-cases';
import { AppStore } from '@app/core/states';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { FeatureFlagsService } from './feature-flags.service';
import { FEATURE_FLAGS } from './feature-flags.constants';

const AUTO_BACKUP_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

@Injectable({ providedIn: 'root' })
export class AutoBackupService {
  private readonly appStore = inject(AppStore);
  private readonly changeTracker = inject(CHANGE_TRACKER);
  private readonly destroyRef = inject(DestroyRef);
  private readonly createBackupUseCase = inject(CreateBackupUseCase);
  private readonly getLastBackupTimeUseCase = inject(GetLastBackupTimeUseCase);
  private readonly featureFlags = inject(FeatureFlagsService);
  private isBackupInProgress = false;
  private lastBackupTime = 0;
  private hasChangesSinceLastBackup = false;

  constructor() {
    // Skip initialization if auto-backup is disabled (e.g., in e2e tests)
    if (this.featureFlags.isEnabled(FEATURE_FLAGS.DISABLE_AUTO_BACKUP)) {
      console.log('✓ Auto-backup disabled via feature flag');
      return;
    }

    // Initialize last backup time on database initialization
    toObservable(this.appStore.isDatabaseInitialized)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (isDatabaseInitialized) => {
        this.lastBackupTime = 0;
        this.hasChangesSinceLastBackup = false;
        if (isDatabaseInitialized) {
          try {
            // Read and cache last backup time
            this.lastBackupTime = await this.getLastBackupTimeUseCase.execute();
            console.log('✓ Auto-backup initialized, last backup:', new Date(this.lastBackupTime).toISOString());
          } catch (error) {
            console.error('Failed to initialize auto-backup state:', error);
          }
        }
      });

    // Track any changes since last backup
    this.changeTracker.hasChanges$
      .pipe(
        filter((hasChanges) => hasChanges),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.hasChangesSinceLastBackup = true;
        this.tryCreateBackup();
      });
  }

  // Backup on application exit
  async tryCreateBackupBeforeExit(): Promise<void> {
    await this.tryCreateBackup();
  }

  private async tryCreateBackup(): Promise<void> {
    if (this.isBackupInProgress) return;
    if (!this.appStore.isDatabaseInitialized()) return;
    if (!this.hasChangesSinceLastBackup) return; // Only backup if there were changes since last backup
    if (Date.now() - this.lastBackupTime < AUTO_BACKUP_INTERVAL_MS) return; // Check 24h interval
    if (!this.appStore.isLeader()) return; // Only leader tab should create auto-backups

    this.isBackupInProgress = true;
    try {
      // Silent mode: no progress UI, no OAuth redirects
      const { backup } = await this.createBackupUseCase.execute({ silent: true });
      this.lastBackupTime = backup.createdAt;
      this.hasChangesSinceLastBackup = false;

      console.log('✓ Auto-backup completed successfully');
    } catch (error) {
      // Don't throw - auto-backup failures should be silent
      console.error('Auto-backup failed:', error);
    } finally {
      this.isBackupInProgress = false;
    }
  }
}
